import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";

import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Icon from "@amzn/meridian/esm/components/icon";
import Link from "@amzn/meridian/esm/components/link";
import Theme from "@amzn/meridian/esm/components/theme";
import Tile from "@amzn/meridian/esm/components/tile";

import alertFilledTokens from "@amzn/meridian-tokens/base/icon/alert-knockout";
import errorKnockout from "@amzn/meridian-tokens/base/icon/error-knockout";
import LogManager from "../../utils/logManager";
import { openUrl } from "../../utils/zoomSdkUtils";
import LearnMoreLink from "../LearnMoreLink";
import { SUMMARY_CLASSIFICATION_TYPE } from "./summary.constants";
import { THEME_COLOR } from "../../styles/colors";

const FIELD_SEPARATOR = "\u001F";

// Icon + text status message.
const SummaryMessage = ({ icon, color, children }) => (
  <Row width="100%" widths={["16px", "fill"]} wrap="none" alignmentHorizontal="start" spacing="300" alignmentVertical="top">
    <Icon tokens={icon} color={color} />
    <Box>
      <Text type="b100">
        {children}
      </Text>
    </Box>
  </Row>
);

// Warning above the summary body, 24px gap below.
const WarningBanner = ({ children }) => (
  <Column spacing="none" spacingInset="none none 450 none">
    <SummaryMessage icon={alertFilledTokens} color="warning">
      {children}
    </SummaryMessage>
  </Column>
);

// A list section's title (teal accent bar), if any, above the list.
const TitledList = ({ title, children }) => (
  title ? (
    <Column spacing="400">
      <Theme
        tokens={{
          tileBorderWidth: 0,
          tileBorderRadius: 0,
          tileBackgroundColor: "transparent",
          tileAccentWidth: 2,
          tileSpacingInset: 0,
        }}
      >
        <Tile accent={THEME_COLOR.LIGHT_TEAL} accentPosition="start">
          <Box spacingInset="none none none 200">
            <Text type="h50">{title}</Text>
          </Box>
        </Tile>
      </Theme>
      {children}
    </Column>
  ) : children
);

// One structured-summary section by format; null for an unknown format.
const renderSection = (section, index) => {
  const { type, format, content, items, title } = section;

  LogManager.info(`renderSection: Processing section ${index} - type="${type}", format="${format}"`);

  switch (format) {
    case "text":
      LogManager.info(`renderSection: Rendering text format`);
      return (
        <Box>
          <Text type="b100">
            {content}
          </Text>
        </Box>
      );

    case "markdown":
      LogManager.info(`renderSection: Rendering markdown format`);
      return (
        <Box>
          <Text type="b100" tag="div">
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
              {content?.replace(/\n/gi, "&nbsp; \n")}
            </ReactMarkdown>
          </Text>
        </Box>
      );

    case "list":
      LogManager.info(`renderSection: Rendering list format with ${items?.length || 0} items`);
      return (
        <TitledList title={title}>
          <Theme tokens={{ textListSpacingVertical: 0 }}>
            <Text tag="ul">
              {items?.map((item, itemIndex) => (
                <li key={itemIndex}>
                  {item.split(/\\n|\n/).map((part, partIndex) => (
                    <Text key={partIndex} type="b100">
                      {part}
                    </Text>
                  ))}
                </li>
              ))}
            </Text>
          </Theme>
        </TitledList>
      );

    case "linkList":
      LogManager.info(`renderSection: Rendering linkList format with ${items?.length || 0} items`);
      return (
        <TitledList title={title}>
          <Theme tokens={{ textListSpacingVertical: 0, linkForegroundColorDefault: THEME_COLOR.TEAL_GREEN, linkForegroundColorHover: THEME_COLOR.TEAL_GREEN, linkForegroundColorPressed: THEME_COLOR.TEAL_GREEN }}>
            <Text tag="ul">
              {items?.map((item, itemIndex) => {
                const [name, url] = item.split(FIELD_SEPARATOR, 2);
                if (!url || !(url.startsWith("http://") || url.startsWith("https://"))) {
                  return null;
                }
                return (
                  <li key={itemIndex}>
                    <Text type="b100">
                      <Link
                        href={url}
                        type="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          openUrl(url, "Failed to open chat link:");
                        }}
                      >
                        {name || url}
                      </Link>
                    </Text>
                  </li>
                );
              })}
            </Text>
          </Theme>
        </TitledList>
      );

    default:
      LogManager.warn(`Unknown section format: ${format} for type: ${type}`);
      return null;
  }
};

// Summary detail body: a status message for error / blocked summaries, otherwise warning banners,
// the structured sections, and the AI footer.
const SummaryBodyInDetailPage = ({ summary, isTopicNotApproved }) => {
  const isAttorneyInAttendance = summary.summaryType === SUMMARY_CLASSIFICATION_TYPE.ATTORNEY_IN_ATTENDANCE;

  // Error / blocked summaries: just the message.
  if (!summary.organizerWarningMessage && summary.iconType && !isAttorneyInAttendance) {
    return (
      <Column spacing="none">
        <SummaryMessage icon={summary.iconType === "warning" ? alertFilledTokens : errorKnockout} color={summary.iconType}>
          {summary.summaryHighlight}
        </SummaryMessage>
        {isTopicNotApproved && (
          <Box spacingInset="400 none none none">
            <LearnMoreLink />
          </Box>
        )}
      </Column>
    );
  }

  return (
    <Column spacing="none">
      {isAttorneyInAttendance && <WarningBanner>Attorney in attendance</WarningBanner>}
      {/* Organizer Warning Message - show and continue */}
      {summary.organizerWarningMessage && <WarningBanner>{summary.organizerWarningMessage}</WarningBanner>}

      {/* Top gap: none for the first section, 24px before a list, 16px otherwise. Unknown formats are skipped. */}
      {summary.structuredSummary?.sections?.map((section, index) => {
        const renderedSection = renderSection(section, index);
        const isListSection = section.format === "list" || section.format === "linkList";
        const spacingInset = index === 0 ? "none" : isListSection ? "450 none none none" : "400 none none none";
        return renderedSection && (
          <Box key={`section-wrap-${index}`} spacingInset={spacingInset}>
            {renderedSection}
          </Box>
        );
      })}

      {/* AIMS Footer - only show if there's any content */}
      {summary.structuredSummary?.sections?.length > 0 && (
        <Column spacing="none" spacingInset="450 none none none">
          <Theme tokens={{ textColorPrimary: THEME_COLOR.MID_GRAY }}>
            <Text type="b100" color="primary">
              <b>Amazon Meetings</b> is powered by Amazon AI. Always check for accuracy.
            </Text>
          </Theme>
        </Column>
      )}
    </Column>
  );
};

export default SummaryBodyInDetailPage;
